import { getSiteBuildManifest, type SiteBuildManifest } from "@/lib/build-id";
import { rememberStaleTabOverlay, STALE_TAB_OVERLAY_FADE_MS } from "./stale-tab-overlay";

const RELOADED_KEY = "cc-deploy-recovery";
const HISTORY_GUARD_KEY = "__ccDeployRecovery";
const FAILURE_EVENT = "cc-stale-tab-failure";
const CHECK_TTL_MS = 30_000;
const RELOAD_GUARD_MS = 5 * 60_000;
export const STALE_TAB_RELOAD_EVENT = "cc-stale-tab-reload";
export const STALE_TAB_RECOVERY_RESET_EVENT = "cc-stale-tab-recovery-reset";

/** Only deployment/chunk errors qualify; application errors must remain visible. */
export function isDeploymentChunkError(error: unknown): boolean {
  const message = typeof error === "string" ? error : error instanceof Error
    ? `${error.name}: ${error.message}` : "";
  return /ChunkLoadError|Loading (?:CSS )?chunk .+ failed|Failed to load chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(message);
}

/** React boundaries consume errors before the global error listeners see them. */
export function requestStaleTabRecovery(error: unknown): void {
  if (typeof window !== "undefined" && isDeploymentChunkError(error)) {
    window.dispatchEvent(new Event(FAILURE_EVENT));
  }
}

function validManifest(value: unknown): value is SiteBuildManifest {
  if (!value || typeof value !== "object") return false;
  const manifest = value as SiteBuildManifest;
  return typeof manifest.buildId === "string" && !!manifest.buildId.trim()
    && !["unknown", "development", "local"].includes(manifest.buildId)
    && typeof manifest.builtAt === "string" && typeof manifest.siteEnv === "string";
}

function isStaticChunkTarget(target: EventTarget | null): boolean {
  return (target instanceof HTMLScriptElement && target.src.includes("/_next/static/"))
    || (target instanceof HTMLLinkElement && target.rel === "stylesheet" && target.href.includes("/_next/static/"));
}

type RecoveryOptions = {
  manifest?: SiteBuildManifest;
  navigate?: (href?: string) => void;
};

/**
 * Check in the background on wake-up and expired link intent. Preserve normal
 * router navigation unless a different deployment is confirmed. Next's
 * deploymentId and chunk-error recovery protect clicks racing that check.
 */
export function installStaleTabRecovery(options: RecoveryOptions = {}): () => void {
  if (typeof window === "undefined" || process.env.NODE_ENV !== "production") return () => {};
  const loaded = options.manifest ?? getSiteBuildManifest();
  const fingerprint = JSON.stringify([loaded.buildId, loaded.builtAt]);
  const navigate = options.navigate ?? ((href?: string) => {
    if (href) window.location.assign(href);
    else window.location.reload();
  });
  let disposed = false;
  let recovering = false;
  let navigating = false;
  let stale = false;
  let lastChecked = 0;
  let pending: Promise<void> | null = null;
  let controller: AbortController | null = null;
  let checkTimer: ReturnType<typeof setTimeout> | undefined;
  let reloadTimer: ReturnType<typeof setTimeout> | undefined;
  let destination: string | undefined;
  let destinationAt = 0;

  function navigateOnce(href?: string) {
    if (disposed || navigating) return;
    navigating = true;
    rememberStaleTabOverlay();
    navigate(href);
  }

  function guardedReload(href?: string) {
    if (disposed || recovering || navigating || !navigator.onLine) return;
    let previous: { fingerprint?: string; at?: number } | null = null;
    try {
      previous = JSON.parse(sessionStorage.getItem(RELOADED_KEY) ?? "null");
    } catch {
      // History state survives reloads even when session storage is unavailable.
      previous = window.history.state?.[HISTORY_GUARD_KEY];
    }
    const historyAttempt = window.history.state?.[HISTORY_GUARD_KEY];
    if (typeof historyAttempt?.at === "number" && historyAttempt.at > (previous?.at ?? 0)) previous = historyAttempt;
    if (previous?.fingerprint === fingerprint && typeof previous.at === "number" && Date.now() - previous.at < RELOAD_GUARD_MS) return;
    const attempt = { fingerprint, at: Date.now() };
    try {
      sessionStorage.setItem(RELOADED_KEY, JSON.stringify(attempt));
    } catch {
      try {
        window.history.replaceState({ ...window.history.state, [HISTORY_GUARD_KEY]: attempt }, "");
      } catch {
        // Without any persistent guard, automatic refresh could become a loop.
        // Link clicks still fall back to document navigation below.
        return;
      }
    }
    recovering = true;
    window.dispatchEvent(new CustomEvent(STALE_TAB_RELOAD_EVENT));
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : STALE_TAB_OVERLAY_FADE_MS + 32;
    reloadTimer = setTimeout(() => navigateOnce(Date.now() - destinationAt < 10_000 ? destination : href), delay);
  }

  function check(): Promise<void> {
    if (pending) return pending;
    if (disposed || !navigator.onLine || !validManifest(loaded)) return Promise.resolve();
    controller = new AbortController();
    checkTimer = setTimeout(() => controller?.abort(), 3_000);
    pending = (async () => {
      try {
        const response = await fetch(`/api/build-id?check=${Date.now()}`, {
          cache: "no-store", credentials: "same-origin", signal: controller!.signal,
        });
        if (!response.ok) return;
        const latest: unknown = await response.json();
        if (disposed || !validManifest(latest)) return;
        lastChecked = Date.now();
        stale = latest.buildId !== loaded.buildId || latest.builtAt !== loaded.builtAt;
        if (stale) guardedReload(Date.now() - destinationAt < 10_000 ? destination : undefined);
      } catch {
        // Offline/temporary API errors must not lock the page or trigger a loop.
      } finally {
        clearTimeout(checkTimer);
        pending = null;
        controller = null;
      }
    })();
    return pending;
  }

  function onResume() {
    if (document.visibilityState === "visible") void check();
  }
  function onPageShow(event: PageTransitionEvent) {
    if (!event.persisted) return;
    // Reset frozen recovery state, then verify in the background. A healthy
    // back/forward snapshot must remain usable without a document reload.
    clearTimeout(reloadTimer);
    recovering = false;
    navigating = false;
    stale = false;
    destination = undefined;
    destinationAt = 0;
    lastChecked = 0;
    window.dispatchEvent(new CustomEvent(STALE_TAB_RECOVERY_RESET_EVENT));
    void check();
  }
  function onClick(event: MouseEvent) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
    if (!anchor || anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return;
    const target = new URL(anchor.href, window.location.href);
    if (target.origin !== window.location.origin || !/^https?:$/.test(target.protocol)) return;
    if (target.pathname === window.location.pathname && target.search === window.location.search && target.hash) return;
    destination = target.href;
    destinationAt = Date.now();
    if (!validManifest(loaded) || !navigator.onLine) return;
    if (stale || recovering || navigating) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // Explicit link intent remains usable if an automatic reload guard is
      // exhausted or unavailable. A scheduled refresh uses the latest intent.
      if (!recovering && !navigating) navigateOnce(destination);
      return;
    }
    if (!pending && Date.now() - lastChecked >= CHECK_TTL_MS) void check();
  }
  function onFailure() {
    guardedReload(Date.now() - destinationAt < 10_000 ? destination : undefined);
  }
  function onError(event: ErrorEvent) {
    if (isStaticChunkTarget(event.target) || isDeploymentChunkError(event.error ?? event.message)) onFailure();
  }
  function onRejection(event: PromiseRejectionEvent) {
    if (isDeploymentChunkError(event.reason)) onFailure();
  }

  window.addEventListener("error", onError, true);
  window.addEventListener("unhandledrejection", onRejection);
  window.addEventListener(FAILURE_EVENT, onFailure);
  window.addEventListener("focus", onResume);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("online", onResume);
  document.addEventListener("visibilitychange", onResume);
  window.addEventListener("click", onClick, true);
  onResume();
  return () => {
    disposed = true;
    controller?.abort();
    clearTimeout(checkTimer);
    clearTimeout(reloadTimer);
    window.removeEventListener("error", onError, true);
    window.removeEventListener("unhandledrejection", onRejection);
    window.removeEventListener(FAILURE_EVENT, onFailure);
    window.removeEventListener("focus", onResume);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("online", onResume);
    document.removeEventListener("visibilitychange", onResume);
    window.removeEventListener("click", onClick, true);
  };
}
